import React, { useEffect, useState } from 'react';
import { Word } from './Word';
import './styles.css';
import { Mode } from '../../types';
import {
  Box,
  Fade,
  Grow,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { MathJax, MathJaxContext } from 'better-react-mathjax';
import { ProperNameShortAnswer } from '@par/shared/types/game';

type PropTypes = {
  answer?: string[][];
  correctAnswer: boolean;
  displaySize: 'sm' | 'md' | 'lg';
  fullQuestion?: string;
  mathFormula?: string;
  mode: Mode;
  properNameShortAnswer?: ProperNameShortAnswer;
  question: string;
  questionImgUrl?: string;
  questionIndex: number;
  spectate?: boolean;
  timerLimitReached: boolean;
  unrevealedIndex?: number[];
};

export const QuestionAnswer = ({
  answer,
  correctAnswer,
  displaySize,
  fullQuestion,
  mathFormula,
  mode,
  properNameShortAnswer,
  question,
  questionImgUrl,
  questionIndex,
  spectate,
  timerLimitReached,
  unrevealedIndex,
}: PropTypes) => {
  const [entireAnswerDisplayed, setEntireAnswerDisplayed] = useState(false);
  const [hideImage, setHideImage] = useState(false);

  useEffect(() => {
    if (!answer) return;
    for (let i = 0; i < answer.length; i++) {
      if (answer[i].includes('')) {
        setEntireAnswerDisplayed(false);
        return;
      }
    }
    setEntireAnswerDisplayed(true);
  }, [answer]);

  useEffect(() => {
    if (entireAnswerDisplayed) {
      setTimeout(() => {
        setHideImage(true);
      }, 4000);
    }
  }, [entireAnswerDisplayed]);

  useEffect(() => {
    setHideImage(false);
  }, [questionIndex]);

  const theme = useTheme();
  const xsBreakpoint = useMediaQuery(theme.breakpoints.only('xs'));

  const underline = (wordIndex: number) => {
    if (!properNameShortAnswer || !answer) return false;
    if (properNameShortAnswer === 'last word') {
      if (wordIndex === answer.length - 1) return true;
    }
    if (properNameShortAnswer === 'last two words') {
      if (wordIndex === answer.length - 1 || wordIndex === answer.length - 2)
        return true;
    }
    if (properNameShortAnswer === 'first word') {
      if (wordIndex === 0) return true;
    }
    return false;
  };

  const displayQuestion = (question: string, fullQuestion?: string) => {
    if (!question && !fullQuestion) return <></>;
    const questionWords = fullQuestion
      ? fullQuestion?.split(' ')
      : question
      ? question.split(' ')
      : [];

    let words = '';
    const elements = [];
    for (const word of questionWords) {
      if (word === '<math-formula>') {
        words = words + ' ';
        elements.push(words);
        elements.push('<math-formula>');
        words = '';
      } else if (word === '<image>') {
        words = words + ' ';
        elements.push(words);
        elements.push('<image>');
        words = '';
      } else {
        words = words + ' ' + word;
      }
    }
    elements.push(words);
    return (
      <>
        {elements.map((e) => {
          if (e === '<math-formula>' && mathFormula) {
            return (
              <MathJax
                style={{ display: 'inline', fontSize: 28 }}
              >{`\\(${mathFormula}\\)`}</MathJax>
            );
          } else if (e === '<image>' && questionImgUrl) {
            return <></>;
          } else {
            return <>{e}</>;
          }
        })}
      </>
    );
  };

  return (
    <MathJaxContext>
      <Stack
        direction="row"
        spacing={questionImgUrl ? 2 : 0}
        justifyContent={questionImgUrl ? 'left' : 'center'}
      >
        {questionImgUrl ? (
          <Box>
            <Fade in={!hideImage} timeout={{ enter: 1500, exit: 500 }}>
              <Box
                component="img"
                src={questionImgUrl}
                alt="image"
                sx={{
                  height: '250px',
                  width: '250px',
                  objectFit: 'cover',
                  filter: 'grayscale(100%)',
                  borderRadius: '5px',
                }}
              />
            </Fade>
          </Box>
        ) : null}
        <Stack spacing={4}>
          {xsBreakpoint ? (
            /* Question on Mobile */
            <Typography textAlign="center" marginY="20px" paddingX={1}>
              {displayQuestion(question, fullQuestion)}
            </Typography>
          ) : (
            /* Question on Desktop */
            <Typography
              textAlign="center"
              variant={
                displaySize === 'sm'
                  ? 'body1'
                  : displaySize === 'md'
                  ? 'h6'
                  : 'h5'
              }
              marginY="20px"
              lineHeight={2}
            >
              {displayQuestion(question, fullQuestion)}
            </Typography>
          )}
          <Stack
            direction="row"
            justifyContent="center"
            spacing={3}
            sx={{ flexWrap: 'wrap', zIndex: 10 }}
          >
            {answer
              ? answer.map((word, wordIndex) => (
                  <Stack
                    direction="row"
                    spacing={1}
                    justifyContent="center"
                    flexWrap={{ xs: 'wrap', sm: 'initial' }}
                    className={`answer-fade-in ${
                      underline(wordIndex) ? '' : ''
                    }`}
                    key={`word-${wordIndex}`}
                  >
                    <Word
                      correctAnswer={correctAnswer}
                      displaySize={displaySize}
                      entireAnswerDisplayed={entireAnswerDisplayed}
                      spectate={spectate}
                      timerLimitReached={timerLimitReached}
                      underline={underline(wordIndex)}
                      unrevealedIndex={unrevealedIndex}
                      word={word}
                      wordIndex={wordIndex}
                    ></Word>
                  </Stack>
                ))
              : null}
          </Stack>
        </Stack>
      </Stack>
    </MathJaxContext>
  );
};
